'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import Image from 'next/image'
import { useBreakpointValue } from '@/hooks/use-mobile'

type BannerConfig = {
  id: string
  name: string
  background: string
}

// `id` matches the asset filename in /assets/SponsorBanner (kept as-is to avoid
// breaking image refs); `name` is the correctly-spelled brand for alt text.
const BANNERS: BannerConfig[] = [
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' },
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' },
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' },
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' },
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' },
  { id: 'Neuroplay', name: 'Neuroplay', background: '#0E0F11' }
]

const ROW_DEFS = [0, 1, 2].map((idx) => ({
  id: `sponsor-row-${idx}`,
  banners: idx % 2 === 0 ? BANNERS : [...BANNERS].reverse(),
  direction: (idx % 2 === 0 ? 'left' : 'right') as 'left' | 'right',
  durationSeconds: 80 + idx * 12
}))

const DIMENSION_TIERS = {
  base: { width: 280, height: 41 },
  sm: { width: 440, height: 64 },
  md: { width: 600, height: 87 },
  lg: { width: 1126.7, height: 163 }
} as const

export default function SponsorsSection() {
  const dimensions = useBreakpointValue(DIMENSION_TIERS)
  const cardWidth = dimensions.width
  const cardHeight = dimensions.height

  return (
    <section className="relative py-12 md:py-24 text-white overflow-hidden">
      <div className="space-y-4 sm:space-y-6 md:space-y-10 w-screen relative left-1/2 -translate-x-1/2 px-0">
        {ROW_DEFS.map((row) => (
          <MarqueeRow key={row.id} row={row} cardWidth={cardWidth} cardHeight={cardHeight} />
        ))}

        <div className="text-center pt-6">
          <p className="text-[9px] sm:text-xs md:text-base font-semibold tracking-[0.4em] text-white/80 uppercase">
            WANT TO COLLABORATE WITH US? CONTACT US NOW!
          </p>
        </div>
      </div>
    </section>
  )
}

type RowDef = (typeof ROW_DEFS)[number]

function MarqueeRow({
  row,
  cardWidth,
  cardHeight
}: {
  row: RowDef
  cardWidth: number
  cardHeight: number
}) {
  // Seamless marquee: two identical banner groups in a flex viewport, each
  // animating its own width + one gap (see `neu-marquee-*` in globals.css), so
  // group 2 lands exactly where group 1 began. No JS measuring, no remount.
  return (
    <div className="relative flex overflow-hidden rounded-[24px] [--gap:0.75rem] sm:[--gap:1.25rem] md:[--gap:2.25rem] gap-[var(--gap)]">
      {[0, 1].map((copy) => (
        <div
          key={copy}
          aria-hidden={copy === 1}
          className="flex w-max shrink-0 gap-[var(--gap)] will-change-transform"
          style={{
            animationName: row.direction === 'right' ? 'neu-marquee-right' : 'neu-marquee-left',
            animationDuration: `${row.durationSeconds}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite'
          }}
        >
          {row.banners.map((banner, bannerIndex) => (
            <BannerCard
              key={`${row.id}-${copy}-${banner.id}-${bannerIndex}`}
              banner={banner}
              cardWidth={cardWidth}
              cardHeight={cardHeight}
            />
          ))}
        </div>
      ))}
      <GradientFade side="left" />
      <GradientFade side="right" />
    </div>
  )
}

function BannerCard({
  banner,
  cardWidth,
  cardHeight
}: {
  banner: BannerConfig
  cardWidth: number
  cardHeight: number
}) {
  return (
    <div
      className="relative flex-shrink-0 rounded-[16px] flex items-center justify-center shadow-[0px_25px_55px_rgba(0,0,0,0.45)] overflow-hidden"
      style={{
        width: `${cardWidth}px`,
        height: `${cardHeight}px`,
        background: banner.background
      }}
    >
      <Image
        src={`/assets/SponsorBanner/${banner.id}.png`}
        alt={`${banner.name} sponsor banner`}
        width={Math.round(cardWidth)}
        height={cardHeight}
        className="w-full h-full object-cover"
        unoptimized
      />
    </div>
  )
}

function GradientFade({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      className={`pointer-events-none absolute inset-y-0 z-10 ${side === 'left' ? 'left-0' : 'right-0'} w-12 md:w-24`}
      style={{
        background:
          side === 'left'
            ? 'linear-gradient(to right, var(--landing-surface), transparent)'
            : 'linear-gradient(to left, var(--landing-surface), transparent)'
      }}
    />
  )
}
